import CollapsibleSection from 'src/componentes/CollapsibleSection';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import Input from 'src/componentes/input';
import ColaboradorSimplificadoLookup from 'src/lookups/ColaboradorSimplificadoLookup';
import { capitalizeFirstLetter } from 'src/utils/string';
import useAuth from 'src/hooks/useAuth';
import PeriodoData from 'src/paginas/data/periodo-data';
import AgingLookup from 'src/lookups/aging';
import ConformidadeLookup from 'src/lookups/ConformidadeRevisao';
import FarolRevisaoLookup from 'src/lookups/farol-risco-ponto-controle-revisao';
import StatusRevisaoLookup from 'src/lookups/status-risco-ponto-controle-revisao';
import Checkbox from 'src/componentes/checkbox';
import TipoEvidenciaLookup from 'src/lookups/tipo-evidencia';
import FiltroAnexoLookup from 'src/lookups/filtro-anexo';
import { matchesSearch } from 'src/componentes/filtro/search-drawer/searchDrawerUtils';

function InformacoesBasicas({ model, config, searchQuery }) {
  const intl = useIntl();
  const { utilizarTipoEvidencia, utilizarAuditoria } = config ?? {};
  const { terms: resources } = useAuth();

  const handleChangeFiltro = (field, value) =>
    model.getModel(field).requestChange(value);

  const isVisible = (label) => matchesSearch(label, searchQuery);

  const labelIdIntegracao = intl.formatMessage({ id: 'idIntegracao' });
  const labelDescricao = intl.formatMessage({ id: 'descricao' });
  const labelResponsavel = capitalizeFirstLetter(resources?.responsavel);
  const labelValidador = intl.formatMessage({ id: 'validador' });
  const labelPeriodo = intl.formatMessage({ id: 'periodo' });
  const labelAging = intl.formatMessage({ id: 'aging' });
  const labelConformidade = intl.formatMessage({ id: 'conformidade' });
  const labelFarois = intl.formatMessage({ id: 'label.farois' });
  const labelStatus = intl.formatMessage({ id: 'status' });
  const labelDesconsiderarConcluido = intl.formatMessage({
    id: 'desconsiderarStatusConcluido',
  });
  const labelTipoEvidencias = utilizarTipoEvidencia
    ? intl.formatMessage({ id: 'tipoEvidencias' })
    : null;
  const labelAnexo = intl.formatMessage({ id: 'anexo' });
  const labelExibirAuditados = utilizarAuditoria
    ? intl.formatMessage({ id: 'exibirAuditados' })
    : null;
  const labelExibirNaoAuditados = utilizarAuditoria
    ? intl.formatMessage({ id: 'exibirNaoAuditados' })
    : null;

  const allLabels = [
    labelIdIntegracao,
    labelDescricao,
    labelResponsavel,
    labelValidador,
    labelPeriodo,
    labelAging,
    labelConformidade,
    labelFarois,
    labelStatus,
    labelDesconsiderarConcluido,
    labelTipoEvidencias,
    labelAnexo,
    labelExibirAuditados,
    labelExibirNaoAuditados,
  ].filter(Boolean);

  if (searchQuery && !allLabels.some((l) => isVisible(l))) return null;

  return (
    <CollapsibleSection
      title={intl.formatMessage({ id: 'label.informacoesBasicas' })}
      defaultOpen={true}
      forceOpen={!!searchQuery}
    >
      <Row>
        {isVisible(labelIdIntegracao) && (
          <Col md={12}>
            <Input model={model.getModel('idIntegracao')} />
          </Col>
        )}
        {isVisible(labelDescricao) && (
          <Col md={12}>
            <Input model={model.getModel('descricao')} />
          </Col>
        )}
        {isVisible(labelResponsavel) && (
          <Col md={12}>
            <ColaboradorSimplificadoLookup
              clearable
              model={{
                label: labelResponsavel,
                value: model.getModel('responsavel').value,
                requestChange: model.getModel('responsavel').requestChange,
              }}
            />
          </Col>
        )}
        {isVisible(labelValidador) && (
          <Col md={12}>
            <ColaboradorSimplificadoLookup
              clearable
              model={model.getModel('validador')}
            />
          </Col>
        )}
        {isVisible(labelPeriodo) && (
          <Col md={12}>
            <PeriodoData
              clearable
              isDrawerFilter
              tipoFiltroData={model.getModel('tipoFiltroData').value}
              dataInicio={model.getModel('dataInicio').value}
              dataTermino={model.getModel('dataTermino').value}
              onChange={handleChangeFiltro}
            />
          </Col>
        )}
        {isVisible(labelAging) && (
          <Col md={12}>
            <AgingLookup
              clearable
              model={{
                label: labelAging,
                value: model.getModel('aging').value,
                requestChange: model.getModel('aging').requestChange,
              }}
            />
          </Col>
        )}
        {isVisible(labelConformidade) && (
          <Col md={12}>
            <ConformidadeLookup
              clearable
              model={model.getModel('conformidade')}
            />
          </Col>
        )}
        {isVisible(labelFarois) && (
          <Col md={12}>
            <FarolRevisaoLookup
              multi
              clearable
              model={model.getModel('farois')}
            />
          </Col>
        )}
        {isVisible(labelStatus) && (
          <Col md={12}>
            <StatusRevisaoLookup clearable model={model.getModel('status')} />
          </Col>
        )}
        {isVisible(labelDesconsiderarConcluido) && (
          <Col md={12}>
            <Checkbox
              model={{
                label: labelDesconsiderarConcluido,
                value: model.getModel('desconsiderarStatusConcluido').value,
                requestChange: model.getModel('desconsiderarStatusConcluido')
                  .requestChange,
              }}
            />
          </Col>
        )}
        {utilizarTipoEvidencia && isVisible(labelTipoEvidencias) && (
          <Col md={12} className="mt-3">
            <TipoEvidenciaLookup
              multi={true}
              model={model.getModel('tipoEvidencias')}
            />
          </Col>
        )}
        {isVisible(labelAnexo) && (
          <Col md={12}>
            <FiltroAnexoLookup
              model={{
                label: labelAnexo,
                value: model.getModel('filtroAnexo').value,
                requestChange: model.getModel('filtroAnexo').requestChange,
              }}
              clearable
            />
          </Col>
        )}
      </Row>
      {utilizarAuditoria && (
        <Row>
          <Col md={12}>
            {isVisible(labelExibirAuditados) && (
              <Checkbox
                style={{ marginTop: 8 }}
                model={{
                  label: intl.formatMessage({ id: 'exibirAuditados' }),
                  value: model.getModel('exibirAuditados').value,
                  requestChange:
                    model.getModel('exibirAuditados').requestChange,
                }}
              />
            )}
            {isVisible(labelExibirNaoAuditados) && (
              <Checkbox
                style={{ marginTop: 8 }}
                model={{
                  label: intl.formatMessage({ id: 'exibirNaoAuditados' }),
                  value: model.getModel('exibirNaoAuditados').value,
                  requestChange:
                    model.getModel('exibirNaoAuditados').requestChange,
                }}
              />
            )}
          </Col>
        </Row>
      )}
    </CollapsibleSection>
  );
}

export default InformacoesBasicas;
